import { useState, useEffect } from 'react';

/**
 * Custom hook for managing wagering system state
 * Handles wagering state, wagering players, and wagering eligibility
 */
const useWagering = (
  scores,
  gameState,
  hasSubmitted,
  currentPlayer,
  judge,
  username,
  round,
  handSize = Infinity,
  baseCardsNeeded = 1
) => {
  const [isWagering, setIsWagering] = useState(false);
  const [wageringPlayers, setWageringPlayers] = useState([]);
  const [canWager, setCanWager] = useState(false);
  // RHP: Enhanced Wagering — how many chips the player intends to/has wagered
  const [chipsWagered, setChipsWagered] = useState(1);

  const myChips = scores[currentPlayer?.id] || scores[currentPlayer?.socketId] || 0;
  // A wager of N chips requires baseCardsNeeded * (1 + N) cards in hand -
  // cap N so that requirement never exceeds what's actually in the hand.
  const handCap = Math.floor(handSize / baseCardsNeeded) - 1;
  const maxChips = Math.max(0, Math.min(myChips, handCap));

  // Update eligibility when relevant state changes
  useEffect(() => {
    updateWagerEligibility();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scores, gameState, hasSubmitted, currentPlayer, judge, handSize, baseCardsNeeded]);

  // Keep the selected chip count within what the player can afford and fit in hand
  useEffect(() => {
    setChipsWagered((prev) => Math.min(Math.max(prev, 1), Math.max(maxChips, 1)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxChips]);

  // Clear wager state on round changes
  useEffect(() => {
    setIsWagering(false);
    setWageringPlayers([]);
    setChipsWagered(1);
  }, [round]);

  const updateWagerEligibility = () => {
    const currentScore = scores[currentPlayer?.id] || scores[currentPlayer?.socketId] || 0;
    const hasMinimumScore = currentScore >= 1;
    const isInPlayingPhase = gameState === 'playing';
    const isNotJudge = username !== (judge?.username || '');
    // Need at least enough cards in hand to cover the minimum 1-chip wager
    const hasEnoughCardsToWager = handCap >= 1;

    setCanWager(
      hasMinimumScore && isInPlayingPhase && isNotJudge && !hasSubmitted && hasEnoughCardsToWager
    );
  };

  return {
    isWagering,
    setIsWagering,
    wageringPlayers,
    setWageringPlayers,
    canWager,
    setCanWager,
    chipsWagered,
    setChipsWagered,
    maxChips: Math.max(maxChips, 1),
  };
};

export default useWagering;
